import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChatContextType } from '@trinity/chat';
import {
  ActiveChatSessionContext,
  AuthorContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { ChatApiClientContext } from '@trinity/chat-components/containers/Chat/contexts';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ActiveBrandDetailsContext } from '@trinity/chat-components/containers/UserBrands/UserBrandsContext';
import {
  ChatAPIProxyClient,
  CustomPageRendererContextProvider,
  type CustomPageArbitraryFilterFields,
  type CustomPageDispatchAction,
} from '@trinity/custom-pages-registry';
import { useCustomPageActionDispatch } from '../Chat/hooks/useCustomPageActionDispatch';
import { useSelectedUserTerritoryId } from '../Chat.Filters/hooks/useSelectedUserTerritoryId';

const noopCustomPageDispatchAction: CustomPageDispatchAction = () => {};
const noopSetArbitraryFilterFields = (): void => {};

type Props = {
  children: ReactNode;
  onDataReadyChange?: (isReady: boolean) => void;
};

export function CustomPageContextContainer({ children, onDataReadyChange }: Props): React.ReactElement {
  const chatApiClient = useContext(ChatApiClientContext);
  const chat = useContext(ActiveChatSessionContext);
  const author = useContext(AuthorContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const activeBrandDetails = useContext(ActiveBrandDetailsContext);
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const dispatchCustomPageAction = useCustomPageActionDispatch();
  const territoryId = useSelectedUserTerritoryId();
  const [isDataReady, setIsDataReady] = useState(false);

  const resolvedChatAuthenticatedClient = chatApiClient?.authenticatedClient;
  const resolvedSubject = chat?.context.type === ChatContextType.Agent ? chat.context.data.subject : null;
  const resolvedUser = author
    ? {
        identifier: author.identifier,
        name: author.profile.name ?? '',
        email: author.profile.email ?? '',
        metadata: author.metadata,
        brandIndications: author.brandIndications,
      }
    : undefined;
  const resolvedBrand = activeBrandDetails ?? undefined;
  const resolvedArbitraryFilterFields = filters?.arbitraryFields ?? {};

  if (!resolvedUser || !resolvedBrand) {
    throw new Error('CustomPageContextContainer requires user and brand context from chat providers.');
  }

  const customPageProxiesClient = useMemo(
    () => (resolvedChatAuthenticatedClient ? new ChatAPIProxyClient(resolvedChatAuthenticatedClient) : null),
    [resolvedChatAuthenticatedClient]
  );

  const fetchUserTerritories = useCallback(() => {
    if (!chatApiClient) {
      throw new Error(
        'CustomPageContextContainer must be used within ChatApiProvider (ChatApiClientContext).'
      );
    }

    return chatApiClient.agentResources.getUserTerritories({ brandIdentifier }).then((res) =>
      res.territories.map((t) => ({
        id: t.territoryKey,
        name: t.name,
      }))
    );
  }, [brandIdentifier, chatApiClient]);

  const setArbitraryFilterFields = (fields: CustomPageArbitraryFilterFields): void => {
    if (!dispatch) return;

    dispatch({
      type: 'filters/updated',
      payload: {
        filters: {
          arbitraryFields: fields,
        },
      },
    });
  };

  const markDataReady = useCallback(() => {
    setIsDataReady(true);
  }, []);

  const markDataLoading = useCallback(() => {
    setIsDataReady(false);
  }, []);

  useEffect(() => {
    onDataReadyChange?.(isDataReady);
  }, [isDataReady, onDataReadyChange]);

  return (
    <CustomPageRendererContextProvider
      fetchUserTerritories={fetchUserTerritories}
      value={{
        proxiesClient: customPageProxiesClient,
        subject: resolvedSubject ?? null,
        user: resolvedUser,
        brand: resolvedBrand,
        territoryId,
        arbitraryFilterFields: resolvedArbitraryFilterFields,
        setArbitraryFilterFields: setArbitraryFilterFields ?? noopSetArbitraryFilterFields,
        dispatchAction: dispatchCustomPageAction ?? noopCustomPageDispatchAction,
        dataReady: {
          isDataReady,
          setDataReady: setIsDataReady,
          markDataReady,
          markDataLoading,
        },
      }}
    >
      {children}
    </CustomPageRendererContextProvider>
  );
}
